"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";

import SectionHeader from "@/components/shared/SectionHeader";
import HorizontalArticleCard from "@/components/cards/HorizontalArticleCard";
import { ArticleListType } from "@/types/article";
import { NewsListBlockType } from "@/types/page";
import { getBlockTitle } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";

interface NewsListBlockProps {
  articles: ArticleListType[];
  block: NewsListBlockType;
  categoryListUrl?: string;
}

export default function NewsListBlock({ articles, block, categoryListUrl }: NewsListBlockProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "en-IN";
  const language = locale?.split("-")[0] || "en";
  const sectionId = block?.title
    ? `section-${block.title.toLowerCase().replace(/\s+/g, "-")}`
    : "news-section";

  const maxCols = block?.maxColumns ?? 2;
  const lgGridCols =
    maxCols <= 1
      ? "lg:grid-cols-1"
      : maxCols === 2
        ? "lg:grid-cols-2"
        : maxCols === 3
          ? "lg:grid-cols-3"
          : maxCols === 4
            ? "lg:grid-cols-4"
            : "lg:grid-cols-2";
  const mdGridCols = maxCols > 2 ? "md:grid-cols-3" : "";
  const gridTopMargin = block?.title || block?.showViewMore ? "mt-8" : "mt-4";

  return (
    <section id={sectionId}>
      {(block?.title || block?.showViewMore) && (
        <div
          className={`flex ${block?.title ? "justify-between" : "justify-end"} items-center w-full`}
        >
          {block?.title && (
            <SectionHeader
              showLogo={false}
              title={getBlockTitle(block?.title, language)}
              {...(categoryListUrl ? { link: categoryListUrl } : {})}
            />
          )}
          {block?.showViewMore && categoryListUrl && (
            <Link
              href={categoryListUrl}
              className="underline dark:text-white text-sm flex-shrink-0"
            >
              {language === "te" ? "మరిన్ని చూడండి" : "View More"}
            </Link>
          )}
        </div>
      )}
      <div
        className={`grid sm:grid-cols-2 ${mdGridCols} ${lgGridCols} gap-x-8 gap-y-5 ${gridTopMargin}`}
        role="list"
        aria-label={`List of ${block?.title || "news"} articles`}
      >
        {articles?.map((article, index) => {
          return (
            <article key={index} role="listitem">
              <HorizontalArticleCard
                article={article}
                index={index}
                variant="md"
                showDate={block?.showDate}
                showCategory={block?.showCategory}
              />
            </article>
          );
        })}
      </div>
    </section>
  );
} 